import {
  toJS,
  runInAction,
  makeAutoObservable,
  observable,
  reaction,
  when,
  IReactionDisposer,
} from "mobx";

import {
  RouteType,
  RouteParamsType,
  RouteQueryParamsType,
} from "types/RouteType";
import { LayoutType } from "types/LayoutType";
import { DialogType } from "types/DialogType";
// Uses the local watchComputed, not @intrasync/utils'.
//
// The library moved watchComputed to the /mobx subpath in v26.7, and that
// subpath is unreachable here: tsconfig sets "moduleResolution": "Node", which
// ignores the package `exports` map, and @intrasync/utils ships no physical
// mobx/ directory — so the import resolves to `any` and silently disables type
// checking at the call site below. Its signature is also stricter than the
// local one (`instance: {[key: string]: unknown}` rejects `this`, and it
// returns `() => void` rather than an IReactionDisposer for `disposers`).
import { watchComputed } from "shared/common/mobx";

import RootStore from "../RootStore";
import { RouteManager } from "./managers/RouteManager";
import { LayoutManager } from "./managers/LayoutManager";
import { HistoryManager, HistoryEntry } from "./managers/HistoryManager";
import { DialogManager } from "./managers/DialogManager";
import { ChannelManager } from "./managers/ChannelManager";
import { PermissionManager } from "./managers/PermissionManager";
import { UrlParser } from "./managers/UrlParser";

declare const window: Window;

interface PopStateEvent extends Event {
  state: unknown;
}

export type routesList = RouteType[];

export type RouterStoreProps = {
  defaultRoute: string;
  debugMode: boolean;
  routes: RouteType[];
  layouts: LayoutType[];
  historyEnabled: true;
  isHashRouter?: boolean;
};

export type RouterStoreConfig = {
  defaultRoute: string;
  html5history: boolean;
};

/**
 * Refactored RouterStore following SOLID principles.
 * Acts as the main orchestrator for routing functionality while delegating
 * specific responsibilities to specialized manager classes.
 */
class RouterStore {
  // Core routing state
  params: RouteParamsType = {};
  queryParams: RouteQueryParamsType = {};
  currentRoute: RouteType | null = null;

  // Application metadata
  appName = "";
  appVersion = "";

  // Configuration
  historyEnabled = true;
  defaultRoute: string = "Home";
  isHashRouter = false;
  debugMode = true;
  config = {
    defaultRoute: "Home",
    html5history: true,
  } as RouterStoreConfig;

  // First load handling
  firstLoadRoute: string | RouteType = "";
  firstLoadParams = {};
  firstLoadQueryParams = {};
  storeLoaded = false;
  routesInitialized = false; // Track when routes are fully initialized

  // Navigation state
  isRouting = false;
  isRunningSideEffects = false;

  // Next route handling
  nextRoute: RouteType | null = null;
  nextPath = "";
  nextParams = {};
  nextQueryParams = {};

  // Manager instances - following Dependency Injection principle
  private routeManager: RouteManager;
  private layoutManager: LayoutManager;
  private historyManager: HistoryManager;
  private dialogManager: DialogManager;
  private channelManager: ChannelManager;
  private permissionManager: PermissionManager;
  private urlParser: UrlParser;

  // Store references and disposers
  rootStore: RootStore;
  private disposers: IReactionDisposer[] = [];
  private timeouts: NodeJS.Timeout[] = [];
  private boundHandlePopState: (event: PopStateEvent) => void;

  constructor(
    rootStore: RootStore,
    routesList: RouteType[],
    layoutsList: LayoutType[],
    options: {
      debugMode: boolean;
      appName: string;
      appVersion: string;
      historyEnabled: boolean;
      config: RouterStoreConfig;
      defaultRoute: string;
    }
  ) {
    this.rootStore = rootStore;
    this.debugMode = options.debugMode || false;
    this.appName = options.appName || "unknown";
    this.appVersion = options.appVersion || "unknown";
    this.historyEnabled = options.historyEnabled || true;
    this.config = options.config || {
      defaultRoute: "Home",
      html5history: true,
    };

    if (options?.defaultRoute && options.defaultRoute !== "Home") {
      this.defaultRoute = options.defaultRoute;
    }

    // Initialize managers - following Dependency Injection principle
    this.routeManager = new RouteManager();
    this.layoutManager = new LayoutManager();
    this.historyManager = new HistoryManager();
    this.dialogManager = new DialogManager();
    this.channelManager = new ChannelManager(this.debugMode);
    this.permissionManager = new PermissionManager(rootStore, this.debugMode);
    this.urlParser = new UrlParser(this.debugMode);

    // Bind event handlers
    this.boundHandlePopState = this.handlePopState.bind(this);

    makeAutoObservable(this, {
      rootStore: false,
      routesInitialized: observable,
      // dialogCloseFunction: false,
      // handleRouterStoreError: false,
      // goTo: false,
      // openDialog: false,
      // closeDialog: false,
      // dialogSetActiveTab: false,
      // toggleForceFullScreenDialog: false,
    });

    this.initializeReactions();
    this.initializeRouterData(routesList, layoutsList);
    this.setupBrowserHistoryHandling();
  }

  /**
   * Initializes MobX reactions for reactive behavior
   */
  private initializeReactions(): void {
    // React to channel service changes
    const channelReaction = reaction(
      () => this.currentServicesInChannels,
      (currentServices: (string | string[])[]) => {
        const services = currentServices
          .flat()
          .filter((service): service is string => typeof service === "string");
        return this.rootStore.setChannelServices(services);
      },
      { fireImmediately: true }
    );
    this.disposers.push(channelReaction);

    // React to route channel changes for websocket subscriptions
    if (this.rootStore.syncChannels) {
      this.debugMode && console.warn("Debug enabling syncChannels");

      const routeChannelReaction = reaction(
        () => this.currentRouteChannels,
        (channels) => {
          if (channels && channels.length) {
            this.channelManager.setRouteChannels();
          }
        },
        { fireImmediately: true, delay: 150 }
      );
      this.disposers.push(routeChannelReaction);
    }

    // React to first route navigation
    const firstRouteDisposer = when(
      () =>
        !!this.historyEnabled &&
        !!this.currentRoute &&
        !!this.currentRoute.path,
      () => {
        if (this.currentRoute) {
          this.historyManager.recordHistory({
            route: this.currentRoute,
            params: toJS(this.params),
          });
        }
      }
    );
    this.disposers.push(firstRouteDisposer);

    // Set up timeout for login page detection
    const loginTimeout = setTimeout(() => {
      if (this.currentRoute?.key === "LoginPage" && this.debugMode) {
        console.warn("User has been on login page for 15 seconds");
      }
    }, 15000);
    this.timeouts.push(loginTimeout);

    // React to readiness for first load handling
    const firstLoadDisposer = when(
      () => this.readyToHandleFirstLoadRoute,
      async () => {
        await this.handleFirstLoadRoute();
      }
    );
    this.disposers.push(firstLoadDisposer);
  }

  /**
   * Initializes route and layout data using managers
   */
  private async initializeRouterData(
    routesList: RouteType[],
    layoutsList: LayoutType[]
  ): Promise<void> {
    await this.layoutManager.setLayouts(
      layoutsList,
      this.config,
      this.rootStore
    );
    await this.routeManager.setRoutes(routesList, this.config, this.rootStore);

    // Mark routes as initialized
    runInAction(() => {
      this.routesInitialized = true;
    });

    // Watch for current path changes
    const pathWatcher = watchComputed(this, "currentPath");
    this.disposers.push(pathWatcher);
  }

  /**
   * Sets up browser history handling for navigation
   */
  private setupBrowserHistoryHandling(): void {
    if (this.historyEnabled && window) {
      window.addEventListener("popstate", this.boundHandlePopState);
    }
  }

  /**
   * Handles first load route determination and navigation
   */
  private async handleFirstLoadRoute(): Promise<void> {
    // Handling first load route
    if (this.firstLoadRoute) {
      // First load route set, navigating
      await this.goTo(
        this.firstLoadRoute,
        this.firstLoadParams,
        this.firstLoadQueryParams
      );
    }
  }

  // ===========================================
  // PUBLIC NAVIGATION METHODS
  // ===========================================

  /**
   * Main navigation method - routes to a specific route with parameters
   * @param route - Route name, route object, or null to use current route
   * @param paramsObj - Parameters for the route
   * @param queryParamsObj - Query parameters for the route
   */
  goTo = async (
    route: string | RouteType | null | undefined,
    paramsObj?: RouteParamsType | null,
    queryParamsObj?: RouteQueryParamsType
  ): Promise<void> => {
    if (route === undefined) {
      this.debugMode && console.warn("goTo called with undefined route");
      return;
    }

    try {
      this.setIsRouting(true);

      let targetRoute: RouteType;

      if (route === null) {
        // Use current route when null is passed
        if (!this.currentRoute) {
          this.debugMode &&
            console.warn(
              "goTo called with null route but no current route exists"
            );
          return;
        }
        targetRoute = this.currentRoute;
      } else if (typeof route === "string") {
        const foundRoute = this.routeManager.findRouteByKey(
          route.replaceAll(" ", "")
        );
        if (!foundRoute) {
          throw new Error(`Route not found: ${route}`);
        }
        targetRoute = foundRoute;
      } else {
        targetRoute = route;
      }

      // Ensure role permissions are loaded before checking
      await this.permissionManager.ensureRolePermissionsLoaded();

      // Check permissions before navigation
      if (!this.permissionManager.checkRoutePermission(targetRoute)) {
        const authReroute = await this.permissionManager.handleAuthReroute(
          targetRoute,
          paramsObj,
          queryParamsObj
        );

        if (authReroute) {
          return this.goTo(
            authReroute.route,
            authReroute.params as RouteParamsType,
            authReroute.queryParams as RouteQueryParamsType
          );
        }
        return;
      }

      // Check if we're navigating to the same route with different parameters
      const isSameRoute =
        this.currentRoute && this.currentRoute.key === targetRoute.key;
      const previousParams = toJS(this.params);
      const previousQueryParams = toJS(this.queryParams);

      // RouterStore.goTo navigation

      // Run exit side effects for current route (unless it's the same route)
      if (!isSameRoute) {
        await this.triggerExitSideEffects();
      }

      // Set the new route state
      await this.setRouteState(targetRoute, paramsObj, queryParamsObj);

      if (isSameRoute) {
        // Same route, different parameters - call onParamsChange
        if (targetRoute.onParamsChange) {
          // Calling onParamsChange for route
          this.setIsRunningSideEffects(true);
          try {
            const context = {
              route: targetRoute,
              params: toJS(this.params),
              previousParams,
              rootStore: this.rootStore,
              queryParams: toJS(this.queryParams),
              previousQueryParams,
              nextPath: this.nextPath,
              manualTrigger: false,
            };
            await targetRoute.onParamsChange(context);
          } finally {
            this.setIsRunningSideEffects(false);
          }
        } else {
          // onParamsChange not defined for route
        }
      } else {
        // Different route - call beforeEnter and onEnter
        // Calling beforeEnter for new route
        await this.triggerEnterSideEffects();
      }

      this.debugMode &&
        console.log(`Navigation completed to: ${targetRoute.key}`);

      this.setIsRouting(false);
    } catch (error) {
      console.error("RouterStore error in goTo:", error);
      if (this.handleRouterStoreError) {
        this.handleRouterStoreError("goTo", error);
      }
      this.setIsRouting(false);
    }
  };

  /**
   * Navigates to a URL string
   * @param url - Full URL to navigate to
   */
  goToUrl(url: string): void {
    const parsed = this.urlParser.parseUrl(url, this.routeManager.routes);
    console.log("goToUrl parsed URL:", parsed);
    if (parsed) {
      this.goTo(parsed.route, parsed.params, parsed.queryParams);
    } else {
      this.debugMode && console.warn(`Could not parse URL: ${url}`);
    }
  }

  /**
   * Navigates back to the previous route in history
   */
  goBack(): void {
    // Safety check to ensure historyManager is initialized
    if (!this.historyManager) {
      console.warn(
        "HistoryManager not initialized, falling back to browser back"
      );
      if (window.history.length > 1) {
        window.history.back();
      }
      return;
    }

    const previousEntry = this.historyManager.getPreviousEntry();
    if (previousEntry && previousEntry.route.key) {
      this.goTo(previousEntry.route.key, previousEntry.params);
    } else {
      // Fallback to browser back or default route
      if (window.history.length > 1) {
        window.history.back();
      } else {
        this.goTo(this.defaultRoute);
      }
    }
  }

  // ===========================================
  // ROUTE STATE MANAGEMENT
  // ===========================================

  /**
   * Sets the route state including route, params, and query params
   */
  private async setRouteState(
    route: RouteType,
    params?: RouteParamsType | null,
    queryParams?: RouteQueryParamsType
  ): Promise<void> {
    runInAction(() => {
      this.currentRoute = route;
      this.params = { ...(route.defaultParams || {}), ...(params || {}) };
      this.queryParams = {
        ...(route.defaultQueryParams || {}),
        ...(queryParams || {}),
      };

      // Set layout based on route
      const layoutPath = route.layoutPath;
      if (layoutPath) {
        const layout = this.layoutManager.findLayoutByPath(layoutPath);
        if (layout) {
          const layoutKey =
            ("key" in layout && typeof layout.key === "string"
              ? layout.key
              : null) || layout.name.replace(/ /g, "");
          this.layoutManager.setCurrentLayout(layoutKey);
        }
      }
    });

    // Record in history
    this.historyManager.recordHistory({
      route,
      params: toJS(this.params),
    });
  }

  /**
   * Sets the first load route information
   */
  setFirstLoad(
    firstLoadRoute: string | RouteType,
    firstLoadParams?: RouteParamsType,
    firstLoadQueryParams?: RouteQueryParamsType
  ): void {
    runInAction(() => {
      this.firstLoadRoute = firstLoadRoute;
      this.firstLoadParams = firstLoadParams || {};
      this.firstLoadQueryParams = firstLoadQueryParams || {};
    });
  }

  /**
   * Loads route information from a URL
   */
  loadRouteFromUrl(url: string): {
    route: RouteType;
    params: RouteParamsType;
    queryParams: RouteQueryParamsType;
  } | null {
    return this.urlParser.parseUrl(url, this.routeManager.routes);
  }

  // ===========================================
  // SIDE EFFECTS MANAGEMENT
  // ===========================================

  /**
   * Triggers enter side effects for the current route
   */
  async triggerEnterSideEffects(manualTrigger: boolean = false): Promise<void> {
    if (!this.currentRoute) {
      // triggerEnterSideEffects called but no current route
      return;
    }

    // Don't check isRunningSideEffects here since we manage it in the goTo method
    // The goTo method should handle the side effects state management

    try {
      this.setIsRunningSideEffects(true);

      const context = {
        route: this.currentRoute,
        params: toJS(this.params),
        rootStore: this.rootStore,
        queryParams: toJS(this.queryParams),
        nextPath: this.nextPath,
        manualTrigger,
      };

      // triggerEnterSideEffects executing

      // Run beforeEnter if defined
      if (this.currentRoute.beforeEnter) {
        // Calling beforeEnter
        const shouldContinue = await this.currentRoute.beforeEnter(context);
        if (shouldContinue === false) {
          // beforeEnter returned false, stopping navigation
          return;
        }
        // beforeEnter completed successfully
      }

      // Run onEnter if defined
      if (this.currentRoute.onEnter) {
        // Calling onEnter
        await this.currentRoute.onEnter(context);
        // onEnter completed successfully
      }
    } catch (error) {
      console.error("❌ Error in triggerEnterSideEffects:", error);
      if (this.handleRouterStoreError) {
        this.handleRouterStoreError("triggerEnterSideEffects", error);
      }
    } finally {
      this.setIsRunningSideEffects(false);
    }
  }

  /**
   * Triggers exit side effects for the current route
   */
  async triggerExitSideEffects(manualTrigger: boolean = false): Promise<void> {
    if (!this.currentRoute || this.isRunningSideEffects) {
      return;
    }

    try {
      this.setIsRunningSideEffects(true);

      const context = {
        route: this.currentRoute,
        params: toJS(this.params),
        rootStore: this.rootStore,
        queryParams: toJS(this.queryParams),
        nextPath: this.nextPath,
        manualTrigger,
      };

      // Run beforeExit if defined
      if (this.currentRoute.beforeExit) {
        const shouldContinue = await this.currentRoute.beforeExit(context);
        if (shouldContinue === false) {
          return;
        }
      }

      // Run onExit if defined
      if (this.currentRoute.onExit) {
        await this.currentRoute.onExit(context);
      }
    } catch (error) {
      if (this.handleRouterStoreError) {
        this.handleRouterStoreError("triggerExitSideEffects", error);
      }
    } finally {
      this.setIsRunningSideEffects(false);
    }
  }

  /**
   * Triggers parameter change side effects for the current route
   */
  async triggerParamsChangeSideEffects(
    manualTrigger: boolean = false
  ): Promise<void> {
    if (!this.currentRoute || this.isRunningSideEffects) {
      return;
    }

    try {
      this.setIsRunningSideEffects(true);

      if (this.currentRoute.onParamsChange) {
        const context = {
          route: this.currentRoute,
          params: toJS(this.params),
          rootStore: this.rootStore,
          queryParams: toJS(this.queryParams),
          nextPath: this.nextPath,
          manualTrigger,
        };

        await this.currentRoute.onParamsChange(context);
      }
    } catch (error) {
      if (this.handleRouterStoreError) {
        this.handleRouterStoreError("triggerParamsChangeSideEffects", error);
      }
    } finally {
      this.setIsRunningSideEffects(false);
    }
  }

  // ===========================================
  // DIALOG MANAGEMENT DELEGATION
  // ===========================================

  /**
   * Opens a dialog - delegates to DialogManager
   */
  openDialog = ({
    dialogName,
    dialogTab,
    dialogContext,
  }: {
    dialogName: string;
    dialogTab?: string;
    dialogContext?: string;
  }): void => {
    if (!this.dialogManager) {
      console.error("DialogManager not initialized");
      return;
    }

    this.dialogManager.openDialog({ dialogName, dialogTab, dialogContext });

    // Update URL query parameters to sync with dialog state
    this.goTo(null, this.params ? toJS(this.params) : null, {
      ...this.queryParams,
      dialogName,
      ...(dialogTab && { dialogTab }),
      ...(dialogContext && { dialogContext }),
    });
  };

  /**
   * Closes the current dialog - delegates to DialogManager
   */
  closeDialog = (): void => {
    console.log("Calling closeDialog");
    console.log("Current query params:", toJS(this.queryParams));
    console.log("Current params:", toJS(this.params));
    if (!this.dialogManager) {
      console.error("DialogManager not initialized");
      return;
    }

    this.dialogManager.closeDialog();

    // Remove dialog-related query parameters from URL
    const remainingQueryParams = Object.fromEntries(
      Object.entries(this.queryParams).filter(
        ([key]) => !["dialogName", "dialogTab", "dialogContext"].includes(key)
      )
    );
    this.goTo(
      null,
      this.params ? toJS(this.params) : null,
      remainingQueryParams
    );
  };

  /**
   * Sets the active dialog tab - delegates to DialogManager
   */
  dialogSetActiveTab = (tabIndex: number): void => {
    if (!this.dialogManager) {
      console.error("DialogManager not initialized");
      return;
    }
    this.dialogManager.setActiveTab(tabIndex);
  };

  /**
   * Toggles force full screen dialog mode - delegates to DialogManager
   */
  toggleForceFullScreenDialog = (): void => {
    if (!this.dialogManager) {
      console.error("DialogManager not initialized");
      return;
    }
    this.dialogManager.toggleForceFullScreenDialog();
  };

  // ===========================================
  // CHANNEL MANAGEMENT DELEGATION
  // ===========================================

  /**
   * Sets up route-specific channels - delegates to ChannelManager
   */
  setRouteChannels(): void {
    this.channelManager.setRouteChannels();
  }

  /**
   * Clears all route-specific channels - delegates to ChannelManager
   */
  clearRouteChannels(): void {
    this.channelManager.clearRouteChannels();
  }

  /**
   * Sets the last channels that were configured - delegates to ChannelManager
   */
  setLastSetChannels(channels: string): void {
    this.channelManager.setLastSetChannels(channels);
  }

  /**
   * Gets the last set channels
   */
  get lastSetChannels(): string {
    return this.channelManager.lastSetChannels;
  }

  // ===========================================
  // BACKWARD COMPATIBILITY COMPUTED PROPERTIES
  // ===========================================

  /**
   * Gets the routes object for backward compatibility
   */
  get routes(): { [key: string]: RouteType } {
    return this.routeManager.routes;
  }

  /**
   * Gets the routes list for backward compatibility
   */
  get routesList(): RouteType[] {
    return this.routeManager.routesList;
  }

  /**
   * Gets the current layout for backward compatibility
   */
  get currentLayout(): LayoutType | null {
    return this.layoutManager.currentLayout;
  }

  /**
   * Gets the router history for backward compatibility
   */
  get routerHistory(): HistoryEntry[] {
    return this.historyManager.getHistoryCopy();
  }

  /**
   * Gets the force full screen dialog state for backward compatibility
   */
  get forceFullScreenDialog(): boolean {
    return this.dialogManager.forceFullScreenDialog;
  }

  /**
   * Dialog close function for backward compatibility
   */
  dialogCloseFunction = (): void => {
    this.closeDialog();
  };

  /**
   * Handles authentication reroute for backward compatibility
   */
  // Arrow-bound like the other public methods on this store (goTo, openDialog,
  // dialogCloseFunction, …). It is destructured off routerStore at the call
  // sites (AuthStore.ts, ServiceStore.ts), so a plain method loses `this` and
  // throws "Cannot read properties of undefined (reading 'permissionManager')"
  // on every auth reroute — which aborts the post-login/registration redirect.
  handleAuthReroute = async (
    route: RouteType,
    params?: object | null,
    queryParams?: object
  ): Promise<{ route: string; params?: object; queryParams?: object } | null> => {
    return await this.permissionManager.handleAuthReroute(
      route,
      params,
      queryParams
    );
  };

  // ===========================================
  // UTILITY METHODS
  // ===========================================

  /**
   * Sets the routing state
   */
  setIsRouting(value: boolean): void {
    runInAction(() => {
      this.isRouting = value;
    });
  }

  /**
   * Sets the side effects running state
   */
  setIsRunningSideEffects(value: boolean): void {
    runInAction(() => {
      this.isRunningSideEffects = value;
    });
  }

  /**
   * Handles browser popstate events
   */
  private handlePopState(event: PopStateEvent): void {
    const state = event.state as {
      routeName?: string;
      params?: RouteParamsType;
    } | null;
    if (state?.routeName) {
      this.goTo(state.routeName, state.params);
    } else {
      // Parse current URL
      this.goToUrl(window.location.href);
    }
  }

  /**
   * Handles errors that occur in the router store
   */
  handleRouterStoreError = (actionName: string, error: unknown): void => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`RouterStore error in ${actionName}:`, errorMessage);

    if (this.rootStore.notificationStore) {
      this.rootStore.notificationStore.addErrorNotification(
        `Navigation error: ${errorMessage}`
      );
    }
  };

  /**
   * Cleans up resources when the store is disposed
   */
  dispose(): void {
    // Dispose all reactions
    this.disposers.forEach((disposer) => disposer());
    this.disposers = [];

    // Clear all timeouts
    this.timeouts.forEach((timeout) => clearTimeout(timeout));
    this.timeouts = [];

    // Remove browser event listeners
    if (window) {
      window.removeEventListener("popstate", this.boundHandlePopState);
    }

    this.debugMode && console.log("RouterStore disposed");
  }

  // ===========================================
  // COMPUTED PROPERTIES
  // ===========================================

  /**
   * Gets the current path for the active route
   */
  get currentPath(): string {
    if (!this.currentRoute) {
      return "";
    }
    return this.urlParser.generateUrl(
      this.currentRoute,
      this.params,
      this.queryParams
    );
  }

  /**
   * Gets the current base path (without parameters)
   */
  get currentBasePath(): string {
    return this.currentRoute?.path || "";
  }

  /**
   * Gets the permission path for the current route
   */
  get currentPermissionPath(): string {
    if (!this.currentRoute) {
      return "";
    }
    return this.channelManager.convertFullPathToPermissionPath(
      this.currentPath
    );
  }

  /**
   * Gets the current route channels for websocket subscriptions
   */
  get currentRouteChannels(): string[] {
    return this.currentRoute?.channels || [];
  }

  /**
   * Gets the current services that should be subscribed to channels
   */
  get currentServicesInChannels(): (string | string[])[] {
    const channels = this.currentRouteChannels;
    return this.channelManager.findChannelUpdates(channels, this.params);
  }

  /**
   * Checks if the current user is an admin
   */
  get isAdminUser(): boolean {
    return this.permissionManager.isUserAdmin();
  }

  /**
   * Checks if the router is ready to handle the first load route
   */
  get readyToHandleFirstLoadRoute(): boolean {
    return !!(
      this.routeManager.routes &&
      Object.keys(this.routeManager.routes).length > 0 &&
      this.layoutManager.layouts &&
      Object.keys(this.layoutManager.layouts).length > 0 &&
      this.firstLoadRoute
    );
  }

  // Dialog computed properties - delegate to DialogManager
  get renderDialog(): DialogType | null {
    if (!this.dialogManager.isDialogOpen) {
      return null;
    }

    // Find the dialog configuration from the current route
    const dialogName = this.dialogManager.currentDialogName;
    if (!this.currentRoute || !this.currentRoute.dialogs) {
      return null;
    }

    const dialogConfig = this.currentRoute.dialogs.find(
      (d) => d.name === dialogName
    );
    return dialogConfig || null;
  }

  get renderDialogTab():
    | { activeTabIndex: number; activeTab?: string }
    | undefined {
    if (!this.dialogManager.isDialogOpen) {
      return undefined;
    }

    return {
      activeTabIndex: this.dialogManager.activeTabIndex,
      activeTab: this.dialogManager.currentDialogTab,
    };
  }

  get dialogFullScreen(): boolean {
    return this.dialogManager.shouldRenderFullScreen;
  }

  get dialogEnableDraggable(): boolean {
    return this.dialogManager.shouldEnableDraggable;
  }

  // Route organization computed properties - delegate to RouteManager
  get routesByPermissionPath(): {
    sectionName: string;
    sections: { name: string; fullPath: string }[];
  }[] {
    return this.routeManager.getRoutesByPermissionPath();
  }

  get routesWithPermissions(): RouteType[] {
    return this.permissionManager.getAccessibleRoutes(
      this.routeManager.routesList
    );
  }
}

export default RouterStore;
